import { TRECHO_MAXIMO_MS, TRECHO_MINIMO_MS } from '@remoto/shared';

/** Silêncio contínuo que fecha um trecho, quando ele já tem tamanho mínimo. */
export const PAUSA_QUE_CORTA_MS = 700;

export interface Quadro {
  /** Energia do quadro (RMS), entre 0 e 1. */
  energia: number;
  /** Fim deste quadro na linha do tempo da gravação. */
  fimMs: number;
}

export interface Corte {
  inicioMs: number;
  fimMs: number;
}

/**
 * Decide onde cortar a fala ao vivo.
 *
 * Três regras, nesta ordem: nunca cortar antes do mínimo que a Groq cobra
 * (dez segundos), cortar numa pausa quando ela aparecer, e cortar de qualquer
 * jeito ao chegar no teto — senão uma fala corrida de cinco minutos viraria
 * uma requisição só, cara e mais sujeita a alucinação.
 *
 * O detector de fala é trocável: aqui entra energia do sinal, e um VAD de
 * verdade encaixa no mesmo lugar sem mudar esta lógica.
 */
export class Cortador {
  private inicioMs = 0;
  private silencioDesdeMs: number | null = null;

  constructor(
    private readonly limiar = 0.012,
    private readonly minimoMs = TRECHO_MINIMO_MS,
    private readonly maximoMs = TRECHO_MAXIMO_MS,
  ) {}

  /** Devolve um corte quando o quadro fecha um trecho; senão, `null`. */
  aoQuadro(quadro: Quadro): Corte | null {
    const duracao = quadro.fimMs - this.inicioMs;
    const falando = quadro.energia >= this.limiar;

    if (falando) {
      this.silencioDesdeMs = null;
    } else if (this.silencioDesdeMs === null) {
      this.silencioDesdeMs = quadro.fimMs;
    }

    const pausaLonga =
      this.silencioDesdeMs !== null && quadro.fimMs - this.silencioDesdeMs >= PAUSA_QUE_CORTA_MS;

    if (duracao >= this.maximoMs || (duracao >= this.minimoMs && pausaLonga)) {
      return this.fechar(quadro.fimMs);
    }
    return null;
  }

  /** Corte final, ao parar de gravar: só se houver algo dentro. */
  encerrar(fimMs: number): Corte | null {
    return fimMs > this.inicioMs ? this.fechar(fimMs) : null;
  }

  private fechar(fimMs: number): Corte {
    const corte = { inicioMs: this.inicioMs, fimMs };
    this.inicioMs = fimMs;
    this.silencioDesdeMs = null;
    return corte;
  }
}

/** Energia média (RMS) de um bloco de amostras normalizadas. */
export function energiaDe(amostras: Float32Array): number {
  if (amostras.length === 0) return 0;
  let soma = 0;
  for (const amostra of amostras) soma += amostra * amostra;
  return Math.sqrt(soma / amostras.length);
}
